/**
 * 新しい版の配信検知
 * 読み込んだ版ID（index.htmlのmeta）と配信中の版ID（/version.json）を比べ、全画面で1つの結果を共有します。
 * 版IDはビルド時に`scripts/build/appBuildIdPlugin.js`が出力内容から作るため、今後のデプロイでも手作業は要りません。
 */

import { logWarn } from '../utils/logger';

/** 配信中の版IDを返すファイル */
const APP_VERSION_URL = '/version.json';
/** 読み込んだ版IDを持つmeta要素 */
const APP_BUILD_ID_META_SELECTOR = 'meta[name="app-build-id"]';
/** 画面を開いたままでも照合する間隔。静的ファイル1件の取得で、Supabaseには問い合わせない。 */
const CHECK_INTERVAL_MS = 5 * 60 * 1000;
/** focusとvisibilitychangeが続けて起きても照合を重ねない最短間隔。 */
const MIN_CHECK_GAP_MS = 30 * 1000;

/** 全画面で共有する検知結果。 */
let snapshot: { isUpdateAvailable: boolean } = { isUpdateAvailable: false };
/** Reactの購読先。 */
const listeners = new Set<() => void>();
/** 最後の購読解除時の停止処理。 */
let stopSync: (() => void) | undefined;
/** 画面遷移の即時再購読では同じ監視を維持する。 */
let stopTimer: ReturnType<typeof setTimeout> | undefined;

/** 検知結果の同一参照スナップショットを返す。 */
export const getAppUpdateSnapshot = () => snapshot;

/**
 * 読み込んだ版IDを返す
 * @returns 版ID。開発サーバーなどmetaが無い場合はnull
 */
const readLoadedBuildId = (): string | null =>
  document.querySelector(APP_BUILD_ID_META_SELECTOR)?.getAttribute('content')?.trim() || null;

/**
 * 配信中の版IDを取得する
 * version.jsonを持たない旧配信ではindex.htmlが返り、JSONとして読めないためnullを返す。
 * @returns 版ID。取得・解釈できない場合はnull
 */
const fetchDeployedBuildId = async (): Promise<string | null> => {
  const response = await fetch(APP_VERSION_URL, { cache: 'no-store' });
  if (!response.ok) return null;
  const body: unknown = await response.json().catch(() => null);
  const buildId = (body as { buildId?: unknown } | null)?.buildId;
  return typeof buildId === 'string' && buildId.trim() ? buildId.trim() : null;
};

/**
 * 版IDを定期的に照合し、新しい版を見つけたら購読先へ知らせる
 * @param loadedBuildId 読み込んだ版ID
 * @returns 照合とタイマーの停止処理
 */
const startAppUpdateSync = (loadedBuildId: string): (() => void) => {
  /** この監視の生存状態。 */
  let isActive = true;
  /** 照合の重複実行を抑止する。 */
  let isChecking = false;
  /** 直前に照合を始めた時刻。 */
  let lastCheckedAt = Number.NEGATIVE_INFINITY;

  /** 新しい版を見つけるまで照合する。見つけた後は再読み込みまで状態が変わらないため止める。 */
  const check = async (): Promise<void> => {
    if (!isActive || isChecking || snapshot.isUpdateAvailable) return;
    if (Date.now() - lastCheckedAt < MIN_CHECK_GAP_MS) return;
    isChecking = true;
    lastCheckedAt = Date.now();
    try {
      const deployedBuildId = await fetchDeployedBuildId();
      if (isActive && deployedBuildId && deployedBuildId !== loadedBuildId) {
        snapshot = { isUpdateAvailable: true };
        listeners.forEach((listener) => listener());
      }
    } catch (error) {
      logWarn('新しい版の配信を確認できませんでした。次の画面復帰か定期確認で再試行します。', {
        errorMessage: error instanceof Error ? error.message : String(error),
      });
    } finally {
      isChecking = false;
    }
  };
  /** 画面が見えているときだけ照合する。 */
  const checkVisible = (): void => {
    if (document.visibilityState !== 'hidden') void check();
  };

  void check();
  const interval = setInterval(checkVisible, CHECK_INTERVAL_MS);
  window.addEventListener('focus', checkVisible);
  window.addEventListener('online', checkVisible);
  document.addEventListener('visibilitychange', checkVisible);
  return () => {
    isActive = false;
    clearInterval(interval);
    window.removeEventListener('focus', checkVisible);
    window.removeEventListener('online', checkVisible);
    document.removeEventListener('visibilitychange', checkVisible);
  };
};

/**
 * 画面から検知結果を購読する
 * @param listener Reactの更新通知
 * @returns 購読解除
 */
export const subscribeAppUpdate = (listener: () => void): (() => void) => {
  clearTimeout(stopTimer);
  listeners.add(listener);
  if (!stopSync) {
    const loadedBuildId = readLoadedBuildId();
    if (loadedBuildId) stopSync = startAppUpdateSync(loadedBuildId);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      // 次に購読されたときに照合し直すため、検知結果も初期化する。
      stopTimer = setTimeout(() => {
        stopSync?.();
        stopSync = undefined;
        snapshot = { isUpdateAvailable: false };
      }, 0);
    }
  };
};
